<template>
  <section class="page-hero relative overflow-hidden">
    <!-- Background Gradient Overlay -->
    <div class="absolute inset-0 bg-gradient-to-br from-[#353899] via-[#2d3185] to-[#0F4640]"></div>
    <div class="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
    <div class="absolute inset-0 bg-[url('@/assets/grid-pattern.svg')] opacity-10"></div>
    
    <!-- Floating Shapes -->
    <div class="absolute inset-0 overflow-hidden">
      <div class="floating-shape shape-1"></div>
      <div class="floating-shape shape-2"></div>
      <div class="floating-shape shape-3"></div>
    </div>

    <!-- Content Container -->
    <div class="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-center min-h-[80vh] py-16 sm:py-20 lg:py-24">
        <div class="text-center max-w-5xl mx-auto">
          
          <!-- Main Title -->
          <h1 class="hero-title text-white mb-6 sm:mb-8 animate-fade-in-up">
            {{ title }}
          </h1>
          
          <!-- Subtitle -->
          <div v-if="subtitle" class="hero-subtitle text-white/80 mb-6 sm:mb-8 animate-fade-in-up-delay-1">
            {{ subtitle }}
          </div>
          
          <!-- Description -->
          <p v-if="description" 
             class="hero-description text-white/90 mb-8 sm:mb-12 leading-relaxed animate-fade-in-up-delay-2"
             :class="{ 'text-right': $i18n.locale === 'ar', 'text-left': $i18n.locale !== 'ar' }"
          >
            {{ description }}
          </p>
          
          <!-- CTA Button -->
          <div v-if="showCta" class="animate-fade-in-up-delay-3">
            <a 
              href="https://admin.salasah.sa/#/login"
              class="hero-cta inline-flex items-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 rounded-xl font-semibold hover:bg-white hover:text-[#353899] transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl"
            >
              {{ ctaText || $t('common.tryNow') }}
              <i :class="$i18n.locale === 'ar' ? 'fas fa-arrow-left mr-3' : 'fas fa-arrow-right ml-3'"></i>
            </a>
          </div>
          
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'PageHero',
  props: {
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      default: null
    },
    description: {
      type: String,
      default: null
    },
    showCta: {
      type: Boolean,
      default: true
    },
    ctaText: {
      type: String,
      default: null
    }
  }
}
</script>

<style scoped>
.page-hero {
  min-height: 80vh;
  position: relative;
}

/* Responsive Typography */
.hero-title {
  font-size: clamp(2rem, 5vw, 4rem);
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.02em;
}

.hero-subtitle {
  font-size: clamp(1rem, 2.5vw, 1.5rem);
  font-weight: 500;
  line-height: 1.4;
}

.hero-description {
  font-size: clamp(1rem, 2vw, 1.25rem);
  line-height: 1.6;
  max-width: 48rem;
  margin: 0 auto;
}

.hero-cta {
  font-size: clamp(0.875rem, 1.5vw, 1rem);
  min-height: 3.5rem;
  backdrop-filter: blur(10px);
}

/* Floating Shapes */
.floating-shape {
  position: absolute;
  border-radius: 50%;
  opacity: 0.1;
  backdrop-filter: blur(2px);
}

.shape-1 {
  width: clamp(200px, 30vw, 400px);
  height: clamp(200px, 30vw, 400px);
  background: linear-gradient(135deg, #ffffff, #e0e7ff);
  top: 10%;
  left: -10%;
  animation: float 20s infinite;
}

.shape-2 {
  width: clamp(150px, 25vw, 300px);
  height: clamp(150px, 25vw, 300px);
  background: linear-gradient(135deg, #ffffff, #f0f9ff);
  top: 60%;
  right: -5%;
  animation: float 25s infinite reverse;
}

.shape-3 {
  width: clamp(100px, 20vw, 250px);
  height: clamp(100px, 20vw, 250px);
  background: linear-gradient(135deg, #ffffff, #ecfdf5);
  bottom: 20%;
  left: 20%;
  animation: float 30s infinite;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0) rotate(0deg);
  }
  25% {
    transform: translateY(-20px) rotate(5deg);
  }
  50% {
    transform: translateY(-10px) rotate(-3deg);
  }
  75% {
    transform: translateY(-30px) rotate(2deg);
  }
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-up {
  animation: fadeInUp 0.8s ease-out;
}

.animate-fade-in-up-delay-1 {
  animation: fadeInUp 0.8s ease-out 0.2s both;
}

.animate-fade-in-up-delay-2 {
  animation: fadeInUp 0.8s ease-out 0.4s both;
}

.animate-fade-in-up-delay-3 {
  animation: fadeInUp 0.8s ease-out 0.6s both;
}

/* Responsive Adjustments */
@media (max-width: 640px) {
  .page-hero {
    min-height: 70vh;
  }
  
  .hero-title {
    margin-bottom: 1rem;
  }
  
  .hero-subtitle {
    margin-bottom: 1.5rem;
  }
  
  .hero-description {
    margin-bottom: 2rem;
    padding: 0 1rem;
  }
}

@media (min-width: 641px) and (max-width: 1024px) {
  .page-hero {
    min-height: 75vh;
  }
  
  .hero-description {
    padding: 0 2rem;
  }
}

@media (min-width: 1025px) {
  .page-hero {
    min-height: 80vh;
  }
}

/* RTL Specific Styles */
[dir="rtl"] .hero-title,
[dir="rtl"] .hero-subtitle,
[dir="rtl"] .hero-description {
  text-align: right;
}

[dir="rtl"] .hero-cta i {
  margin-right: 0.75rem;
  margin-left: 0;
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .hero-cta {
    border-color: rgba(255, 255, 255, 0.4);
  }
}
</style> 